import { useState } from 'react';
import copy from 'copy-to-clipboard';
import { generatePath, NavLink, useMatch } from 'react-router-dom';
import {
  formatPhone,
  IconClose,
  IconCopyTwo,
  IconLock,
  IconSelected,
} from '@ui/src';
import { IndividualRightSideContentTypes } from './types';
import { OrganizationType } from '../../hoc/common/types';

import ROUTES from '../../routes';

import cn from '../common/info-right-side/info-right-side.module.css';

const getCurrentAge = (birthDate: string) => {
  const today = new Date();
  const birthDateObj = new Date(birthDate);

  let age = today.getFullYear() - birthDateObj.getFullYear();
  const monthDiff = today.getMonth() - birthDateObj.getMonth();

  if (
    monthDiff < 0 ||
    (monthDiff === 0 && today.getDate() < birthDateObj.getDate())
  ) {
    age -= 1;
  }

  return age;
};

const IndividualRightSideContent = ({
  firstName,
  lastName,
  dateOfBirth,
  phone,
  email,
  type = OrganizationType.INDIVIDUAL,
  activeLock,
  customerId,
  totpEnabled,
}: IndividualRightSideContentTypes): React.JSX.Element => {
  const [emailCopySuccess, setEmailCopySuccess] = useState(false);
  const [phoneCopySuccess, setPhoneCopySuccess] = useState(false);

  const match = useMatch(ROUTES.CUSTOMERS_INDIVIDUAL_SECURITY_LOCKS);

  const onCopy = ({
    value,
    valueType,
  }: {
    value: string;
    valueType: 'email' | 'phone';
  }) => {
    copy(value);

    if (valueType === 'email') {
      setEmailCopySuccess(true);

      setTimeout(() => {
        setEmailCopySuccess(false);
      }, 2000);
    } else {
      setPhoneCopySuccess(true);

      setTimeout(() => {
        setPhoneCopySuccess(false);
      }, 2000);
    }
  };

  return (
    <>
      <div className={cn.nameWrapper}>
        <div className={cn.nameWrapperTitle}>
          {type === OrganizationType.COMPANY_OFFICIAL
            ? 'Company official'
            : 'Individual customer'}
        </div>
        <div className={cn.nameWrapperValue}>{`${firstName} ${lastName}, ${
          dateOfBirth ? getCurrentAge(dateOfBirth) : '-'
        } y.o`}</div>
        <div className={cn.totpWrapper}>
          {totpEnabled ? (
            <IconSelected width={14} height={14} className={cn.enabled} />
          ) : (
            <IconClose width={17} height={17} />
          )}
          TOTP-confirmation {totpEnabled ? 'enabled' : 'not enabled'}
        </div>
      </div>
      {activeLock && (
        <NavLink
          to={`${generatePath(ROUTES.CUSTOMERS_INDIVIDUAL_SECURITY_LOCKS, {
            customerId: (customerId as string) || '',
          })}?locked=true`}
          className={`${cn.locked} ${match ? cn.disabled : ''}`}
        >
          <IconLock width={16} height={16} />
          <span>locked</span>
        </NavLink>
      )}
      <div className={cn.valuesRow}>
        <div className={cn.copyValueRow}>
          <div className={cn.copyValueRowText}>
            {phone ? formatPhone(phone) : ''}
          </div>
          <button
            type="button"
            className={`${cn.copyValueButton} ${
              phoneCopySuccess ? cn.success : ''
            }`}
            aria-label="Copy phone button"
            onClick={() =>
              onCopy({
                value: phone ? formatPhone(phone) : '',
                valueType: 'phone',
              })
            }
          >
            <IconCopyTwo width={14} height={14} />
          </button>
        </div>
        <div className={cn.copyValueRow}>
          <div className={cn.copyValueRowText}>{email}</div>
          <button
            type="button"
            className={`${cn.copyValueButton} ${
              emailCopySuccess ? cn.success : ''
            }`}
            aria-label="Copy email button"
            onClick={() => onCopy({ value: email, valueType: 'email' })}
          >
            <IconCopyTwo width={14} height={14} />
          </button>
        </div>
      </div>
    </>
  );
};

export default IndividualRightSideContent;
